import { useMemo, useState } from "react";

import "./ProductPillars.css";

const PILLARS = [
  {
    id: "custody",
    label: "Custody & Identity",
    short: "Custody",
    hue: "#7B7CFF",
    lead: "Self-custody that doesn't feel like self-custody. Sign in, manage wallets, and connect to dApps without friction.",
    titles: ["Social Login", "Multi-Account Management", "WalletConnect"],
    screenshot: "Portfolio overview",
  },
  {
    id: "trade",
    label: "Trade & Move",
    short: "Trade",
    hue: "#5758FC",
    lead: "Everything that moves value — cross-chain swaps, fiat on-ramps, and payment rails — inside one flow.",
    titles: ["Cross-Chain Swaps", "Fiat On/Off Ramp", "Payment Cards", "Bill Pay"],
    screenshot: "Cross-chain swap flow",
  },
  {
    id: "intel",
    label: "Research & Intelligence",
    short: "Intel",
    hue: "#8C8DFF",
    lead: "Charts, tokenomics, market pulse, and an AI assistant — so research never pulls you out of the wallet.",
    titles: ["Token Intelligence", "Market Overview Dashboard", "MindVault AI"],
    screenshot: "MindVault AI",
  },
  {
    id: "next",
    label: "Everyday & Alerts",
    short: "Everyday",
    hue: "#2BD56F",
    lead: "The everyday surface — your portfolio at a glance and alerts that watch the market for you.",
    titles: ["Portfolio Tracker", "Price Alerts"],
    screenshot: "Price alerts",
  },
];

function badgeClass(badge) {
  if (badge === "Coming soon") return "is-soon";
  if (badge === "Live + expanding") return "is-expanding";
  return "is-live";
}

export default function ProductPillars({ features, showcase }) {
  const [activeId, setActiveId] = useState(PILLARS[0].id);

  const featuresByTitle = useMemo(
    () => new Map(features.map((f) => [f.title, f])),
    [features],
  );
  const showcaseByTitle = useMemo(
    () => new Map(showcase.map((s) => [s.title, s])),
    [showcase],
  );

  const activePillar = PILLARS.find((p) => p.id === activeId) ?? PILLARS[0];

  return (
    <section className="deferred-section pillars-section relative px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
      <div className="mx-auto max-w-7xl">
        <div className="pillars-heading">
          <h2
            className="font-syne text-white"
            style={{ fontSize: "clamp(1.8rem, 3.8vw, 3rem)", lineHeight: "1.05", fontWeight: 700 }}
          >
            The full BiorVault{" "}
            <span className="hero-alive-accent">product surface.</span>
          </h2>
          <p className="pillars-heading__sub font-dm">
            Twelve features, four pillars, one wallet. Pick a pillar to see what
            lives inside.
          </p>
        </div>

        <div
          className="pillars-deck"
          style={{ "--pillar-hue": activePillar.hue }}
          role="region"
          aria-label="BiorVault product pillars"
        >
          <div className="pillars-deck__tabs" role="tablist">
            {PILLARS.map((pillar) => (
              <button
                type="button"
                key={pillar.id}
                role="tab"
                aria-selected={activeId === pillar.id}
                className={`pillars-tab ${activeId === pillar.id ? "is-active" : ""}`}
                style={{ "--tab-hue": pillar.hue }}
                onClick={() => setActiveId(pillar.id)}
              >
                <span className="pillars-tab__dot" aria-hidden="true" />
                <span className="pillars-tab__label font-dm">
                  <span className="pillars-tab__label-full">{pillar.label}</span>
                  <span className="pillars-tab__label-short">{pillar.short}</span>
                </span>
                <span className="pillars-tab__count font-dm">
                  {pillar.titles.length}
                </span>
              </button>
            ))}
          </div>

          <div className="pillars-deck__panel">
            {PILLARS.map((pillar) => {
              const isActive = pillar.id === activeId;
              const pillarFeatures = pillar.titles
                .map((t) => featuresByTitle.get(t))
                .filter(Boolean);
              const pillarShot = showcaseByTitle.get(pillar.screenshot);

              return (
                <div
                  key={pillar.id}
                  className={`pillars-panel ${isActive ? "is-active" : ""}`}
                  role="tabpanel"
                  aria-hidden={!isActive}
                  style={{ "--pillar-hue": pillar.hue }}
                >
                  <div className="pillars-panel__intro">
                    <div className="pillars-panel__eyebrow font-dm">
                      {pillar.label}
                    </div>
                    <p className="pillars-panel__lead font-dm">{pillar.lead}</p>

                    <ul className="pillars-panel__list">
                      {pillarFeatures.map((feature) => (
                        <li key={feature.title} className="pillars-feature">
                          <div className="pillars-feature__icon">
                            <img src={feature.icon} alt="" aria-hidden="true" />
                          </div>
                          <div className="pillars-feature__body">
                            <div className="pillars-feature__top">
                              <span className="pillars-feature__title font-syne">
                                {feature.title}
                              </span>
                              <span
                                className={`pillars-feature__badge ${badgeClass(feature.badge)}`}
                              >
                                {feature.badge}
                              </span>
                            </div>
                            <p className="pillars-feature__desc font-dm">
                              {feature.desc}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pillars-panel__visual" aria-hidden="true">
                    <div className="pillars-panel__frame">
                      {pillarShot && (
                        <img
                          src={pillarShot.image}
                          alt={pillarShot.alt}
                          loading="lazy"
                          decoding="async"
                          className="pillars-panel__screen"
                        />
                      )}
                    </div>
                    <div className="pillars-panel__caption font-dm">
                      {pillarShot?.caption}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Off-screen mount for the remaining screenshots so they stay in the
                DOM with their alt + loading attributes. The active pillar's
                screenshot is already rendered above. */}
            <div className="pillars-deck__shadow-mount" aria-hidden="true">
              {showcase
                .filter((s) => s.title !== activePillar.screenshot)
                .map((s) => (
                  <img
                    key={s.title}
                    src={s.image}
                    alt={s.alt}
                    loading="lazy"
                    decoding="async"
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
